import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Download, Upload, Search, ArrowUpDown } from 'lucide-react';
import { api, Account } from '../../lib/api';
import { exportToCsv, parseCsv, CsvColumn } from '../../lib/csv';

const accountCsvColumns: CsvColumn[] = [
  { key: 'code', label: 'コード' },
  { key: 'name', label: '科目名' },
  { key: 'accountType', label: '科目区分' },
  { key: 'category', label: 'カテゴリ' },
  { key: 'taxType', label: '税区分' },
];

export function AccountListPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [keyword, setKeyword] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortField, setSortField] = useState<'code' | 'name'>('code');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const { data, isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<{ data: Account[] }>('/api/accounts'),
  });

  const importMutation = useMutation({
    mutationFn: (data: any[]) => api.post('/api/accounts/import', { data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      alert('インポートが完了しました');
    },
    onError: () => {
      alert('インポートに失敗しました');
    },
  });

  const typeLabels: Record<string, string> = { asset: '資産', liability: '負債', equity: '純資産', revenue: '収益', expense: '費用' };

  const handleExportCsv = () => {
    if (data?.data) {
      exportToCsv(data.data, accountCsvColumns, '勘定科目マスタ');
    }
  };

  const handleImportCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const parsed = parseCsv<Account>(text, accountCsvColumns);
      if (parsed.length > 0) {
        importMutation.mutate(parsed);
      } else {
        alert('有効なデータがありません');
      }
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // フィルター & ソート
  const filteredData = (data?.data || [])
    .filter((acc) => {
      if (typeFilter && acc.accountType !== typeFilter) return false;
      if (keyword && !acc.name.includes(keyword) && !acc.code.includes(keyword)) return false;
      return true;
    })
    .sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      const cmp = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>勘定科目マスタ</h1>
        <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium" style={{ backgroundColor: 'var(--accent-color)' }}>
          <Plus size={18} />新規登録
        </button>
      </div>

      {/* フィルター */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 max-w-xs">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: 'var(--text-muted)' }}
            />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="コード・科目名で検索..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border text-sm"
              style={{
                backgroundColor: 'var(--input-bg)',
                borderColor: 'var(--input-border)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{
              backgroundColor: 'var(--input-bg)',
              borderColor: 'var(--input-border)',
              color: 'var(--text-primary)',
            }}
          >
            <option value="">全科目区分</option>
            <option value="asset">資産</option>
            <option value="liability">負債</option>
            <option value="equity">純資産</option>
            <option value="revenue">収益</option>
            <option value="expense">費用</option>
          </select>

          <div className="flex items-center gap-2 ml-auto">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleImportCsv}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importMutation.isPending}
              className="px-4 py-2 rounded-lg border text-sm disabled:opacity-50"
              style={{
                borderColor: 'var(--border-color)',
                color: 'var(--text-primary)',
              }}
            >
              <Upload size={16} className="inline mr-1" />
              CSVインポート
            </button>
            <button
              onClick={handleExportCsv}
              disabled={!data?.data?.length}
              className="px-4 py-2 rounded-lg border text-sm disabled:opacity-50"
              style={{
                borderColor: 'var(--border-color)',
                color: 'var(--text-primary)',
              }}
            >
              <Download size={16} className="inline mr-1" />
              CSV出力
            </button>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" /></div>
        ) : (
          <table>
            <thead><tr>
              <th 
                className="cursor-pointer select-none"
                onClick={() => toggleSort('code')}
                style={{ color: 'var(--text-primary)' }}
              >
                <span className="flex items-center gap-1">
                  コード
                  <ArrowUpDown size={14} className={sortField === 'code' ? 'opacity-100' : 'opacity-30'} />
                </span>
              </th>
              <th 
                className="cursor-pointer select-none"
                onClick={() => toggleSort('name')}
                style={{ color: 'var(--text-primary)' }}
              >
                <span className="flex items-center gap-1">
                  科目名
                  <ArrowUpDown size={14} className={sortField === 'name' ? 'opacity-100' : 'opacity-30'} />
                </span>
              </th>
              <th style={{ color: 'var(--text-primary)' }}>科目区分</th>
              <th style={{ color: 'var(--text-primary)' }}>カテゴリ</th>
              <th style={{ color: 'var(--text-primary)' }}>税区分</th>
              <th style={{ color: 'var(--text-primary)' }}>状態</th>
            </tr></thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                    データがありません
                  </td>
                </tr>
              ) : (
                filteredData.map((acc) => (
                  <tr key={acc.id}>
                    <td className="font-mono" style={{ color: 'var(--text-primary)' }}>{acc.code}</td>
                    <td style={{ color: 'var(--text-primary)' }}>{acc.name}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{typeLabels[acc.accountType] || acc.accountType}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{acc.category || '-'}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{acc.taxType || '-'}</td>
                    <td><span className={`badge ${acc.isActive ? 'badge-success' : 'badge-error'}`}>{acc.isActive ? '有効' : '無効'}</span></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
